import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { getQuickFormDefinition } from '../../data/clinicalAssessmentQuickFormDefinitions';
import LocalStorageService from '../../services/storage/LocalStorageService';

function draftStorageKey(slug, residentId) {
  return `quickform_draft_${slug}_${residentId ?? 'none'}`;
}

/**
 * Generic quick capture driven by clinicalAssessmentQuickFormDefinitions.
 * @param {{ slug: string, catalogItem: { name: string, summary?: string }, residentName?: string, residentId?: string|number, onClose: () => void, onComplete?: (payload: object) => void }} props
 */
export default function ClinicalCatalogQuickForm({ slug, catalogItem, residentName = '', residentId, onClose, onComplete }) {
  const def = useMemo(() => getQuickFormDefinition(slug), [slug]);
  const [values, setValues] = useState(() =>
    def ? Object.fromEntries(def.fields.map((f) => [f.id, ''])) : {}
  );
  const [errors, setErrors] = useState({});
  const skipAutosave = useRef(false);

  useEffect(() => {
    if (!def) return;
    const defaults = Object.fromEntries(def.fields.map((f) => [f.id, '']));
    const saved = LocalStorageService.get(draftStorageKey(slug, residentId), null);
    if (saved?.values && typeof saved.values === 'object') {
      setValues({ ...defaults, ...saved.values });
    } else {
      setValues(defaults);
    }
    setErrors({});
    skipAutosave.current = true;
  }, [slug, def, residentId]);

  useEffect(() => {
    if (!def) return;
    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }
    const t = setTimeout(() => {
      LocalStorageService.set(draftStorageKey(slug, residentId), {
        values,
        updatedAt: new Date().toISOString(),
      });
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[ClinicalCatalogQuickForm] draft autosaved', { slug, residentId });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [values, slug, residentId, def]);

  const setField = useCallback((id, v) => {
    setValues((prev) => ({ ...prev, [id]: v }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    if (!def) return { ok: false, next: {} };
    /** @type {Record<string, string>} */
    const next = {};
    for (const f of def.fields) {
      const raw = values[f.id];
      const str = raw === undefined || raw === null ? '' : String(raw).trim();
      if (f.required && str === '') {
        next[f.id] = 'Required';
        continue;
      }
      if (f.type === 'number' && str !== '') {
        const n = Number(str);
        if (Number.isNaN(n)) {
          next[f.id] = 'Enter a valid number';
          continue;
        }
        if (f.min != null && n < f.min) next[f.id] = `Min ${f.min}`;
        if (f.max != null && n > f.max) next[f.id] = `Max ${f.max}`;
      }
    }
    if (slug === 'hand-hygiene-audit') {
      const opp = Number(values.opportunities);
      const comp = Number(values.compliant);
      if (!Number.isNaN(opp) && !Number.isNaN(comp) && comp > opp) {
        next.compliant = 'Cannot exceed opportunities';
      }
    }
    setErrors(next);
    return { ok: Object.keys(next).length === 0, next };
  }, [def, slug, values]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!def) return;
    const { ok, next } = validate();
    if (!ok) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[ClinicalCatalogQuickForm] validation failed', { slug, fields: Object.keys(next) });
      }
      return;
    }
    const payload = {
      tool: 'catalog-quick-form',
      slug,
      assessmentName: catalogItem.name,
      values: { ...values },
      residentName,
      savedAt: new Date().toISOString(),
    };
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[ClinicalCatalogQuickForm] saved (demo)', payload);
    }
    LocalStorageService.remove(draftStorageKey(slug, residentId));
    onComplete?.(payload);
    onClose();
  };

  if (!def) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-700">No quick form definition for slug: {slug}</p>
        <button type="button" onClick={onClose} className="mf-btn-secondary w-full">
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {def.intro ? <p className="text-sm text-gray-600">{def.intro}</p> : null}
      <p className="text-xs text-gray-500">
        {residentName ? (
          <>
            Resident: <strong>{residentName}</strong>
          </>
        ) : (
          'No resident context (facility browse).'
        )}{' '}
        · Spec: Compliance.md §6-7 ·{' '}
        <span className="text-indigo-700 font-medium">Draft autosave (local)</span>
      </p>

      {def.fields.map((f) => {
        const err = errors[f.id];
        const baseClass =
          'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]';
        const errClass = err ? ' border-red-400' : ' border-surface-200';

        return (
          <fieldset key={f.id} className="space-y-1">
            <legend className="mf-label text-gray-800">{f.label}</legend>
            {f.type === 'select' && (
              <select
                value={values[f.id] ?? ''}
                onChange={(e) => setField(f.id, e.target.value)}
                className={baseClass + errClass}
                aria-invalid={!!err}
                aria-describedby={err ? `${f.id}-err` : undefined}
              >
                <option value="">— Select —</option>
                {(f.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {f.type === 'text' && (
              <input
                type="text"
                value={values[f.id] ?? ''}
                onChange={(e) => setField(f.id, e.target.value)}
                placeholder={f.placeholder}
                className={baseClass + errClass}
                aria-invalid={!!err}
                aria-describedby={err ? `${f.id}-err` : undefined}
              />
            )}
            {f.type === 'number' && (
              <input
                type="number"
                value={values[f.id] ?? ''}
                onChange={(e) => setField(f.id, e.target.value)}
                min={f.min}
                max={f.max}
                step={f.step ?? 1}
                placeholder={f.placeholder}
                className={baseClass + errClass}
                aria-invalid={!!err}
                aria-describedby={err ? `${f.id}-err` : undefined}
              />
            )}
            {f.type === 'textarea' && (
              <textarea
                value={values[f.id] ?? ''}
                onChange={(e) => setField(f.id, e.target.value)}
                rows={f.rows ?? 3}
                placeholder={f.placeholder}
                className={`${baseClass} min-h-[5rem] py-2 ${errClass}`}
                aria-invalid={!!err}
                aria-describedby={err ? `${f.id}-err` : undefined}
              />
            )}
            {err ? (
              <p id={`${f.id}-err`} className="mf-field-error text-xs">
                {err}
              </p>
            ) : null}
          </fieldset>
        );
      })}

      <div className="flex gap-2 pt-2">
        <button type="button" onClick={onClose} className="mf-btn-secondary flex-1">
          Cancel
        </button>
        <button type="submit" className="mf-btn-primary flex-1">
          Save (demo)
        </button>
      </div>
    </form>
  );
}
